import { type OrderTab } from '@/features/orders/orderRecord';
import {
    XERO_SYNC_CHECK,
    XERO_SYNC_KIND,
    type XeroSyncFinding,
    type XeroSyncSide,
} from '@/features/reports/xero/api';
import { formatMoney } from '@/lib/format';

/*
  How the Xero sync tab writes a finding (ALS-388): each side as money or words, when something
  happened in London's time, which is the team's day and the one the nightly run keeps, and where a
  finding opens in the admin.
*/

const LONDON = 'Europe/London';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: LONDON,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
});

function inLondon(date: Date) {
    const out: Record<string, number> = {};
    for (const part of parts.formatToParts(date)) {
        if (part.type !== 'literal') out[part.type] = Number(part.value);
    }
    return out as { year: number; month: number; day: number; hour: number; minute: number };
}

const dayNumber = (t: { year: number; month: number; day: number }) =>
    Date.UTC(t.year, t.month - 1, t.day) / 86_400_000;

const clock = (t: { hour: number; minute: number }) =>
    `${String(t.hour).padStart(2, '0')}:${String(t.minute).padStart(2, '0')}`;

/** "today at 02:00", "yesterday at 02:00", "tomorrow at 02:00", else "27 Oct 2026, 02:00", in London. */
export function whenText(iso: string, now: Date = new Date()): string {
    const at = inLondon(new Date(iso));
    const days = dayNumber(at) - dayNumber(inLondon(now));
    const time = clock(at);
    if (days === 0) return `today at ${time}`;
    if (days === 1) return `tomorrow at ${time}`;
    if (days === -1) return `yesterday at ${time}`;
    return `${at.day} ${MONTHS[at.month - 1]} ${at.year}, ${time}`;
}

/** "27 Oct 2026": the day a finding was first found. */
export function dayText(iso: string): string {
    const at = inLondon(new Date(iso));
    return `${at.day} ${MONTHS[at.month - 1]} ${at.year}`;
}

/** One side of a finding: its amount in the finding's currency, its words, or both; a dash for neither. */
export function sideText(side: XeroSyncSide, currency: string | null): string {
    const amount = side.amount === null ? null : formatMoney(side.amount, currency ?? 'GBP');
    return [amount, side.text].filter((part): part is string => !!part).join(' · ') || '–';
}

/** What a finding is called in a control's accessible name: its document, else its order or client. */
export function findingName(finding: XeroSyncFinding): string {
    return (
        finding.document ??
        finding.order?.name ??
        finding.client?.name ??
        `difference ${finding.id}`
    );
}

/**
 * Where a finding opens in the admin: an order on the tab its money is on, a client on their money
 * tab for the wallet, else their profile; null for a finding about neither.
 */
export function adminLink(
    finding: XeroSyncFinding,
):
    | { to: '/orders/$orderId'; params: { orderId: string }; search: { tab?: OrderTab } }
    | { to: '/customers/$customerId'; params: { customerId: string }; search: { tab?: 'money' } }
    | null {
    if (finding.order) {
        const money =
            finding.check === XERO_SYNC_CHECK.AmountsDue ||
            finding.check === XERO_SYNC_CHECK.Payments ||
            finding.check === XERO_SYNC_CHECK.Allocations;
        return {
            to: '/orders/$orderId',
            params: { orderId: String(finding.order.id) },
            search: money ? { tab: 'payments' } : {},
        };
    }
    if (finding.client) {
        return {
            to: '/customers/$customerId',
            params: { customerId: String(finding.client.id) },
            search: finding.kind === XERO_SYNC_KIND.Wallet ? { tab: 'money' } : {},
        };
    }
    return null;
}
